import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import Constants from 'expo-constants';
import { Profile } from 'store/slices/profileSlice';
import {
  BackendApiLogin,
  BackendApiLoginParams,
  BackendApiPin,
  BackendApiProfile,
  BackendFavorite,
  BackendGenericList,
  BackendWatching,
  FeedbackProps,
  GetWatchingListParams,
} from 'types';
import { Platform } from 'utils/platform';
import { handleBaseQuery } from 'utils/request';

import { cmsApiSlice } from '../cms';
import { mapBackendProfileToFrontend } from './profileMapper';

type BackendUrlState = {
  cmsApi?: { queries?: Record<string, { data?: { enable_cms_prod?: boolean } }> };
  auth?: { userToken?: string };
};

const selectBackendBaseUrl = (state: BackendUrlState) => {
  const globalConfigResult = cmsApiSlice.endpoints.getGlobalConfig.select(undefined)(
    state as never
  );

  const enableCmsProd = true;
  return enableCmsProd
    ? Constants.expoConfig?.extra?.backendApi
    : Constants.expoConfig?.extra?.backendApiHML;
};

const rawBaseQuery = (baseUrl: string) =>
  fetchBaseQuery({
    baseUrl: baseUrl || Constants.expoConfig?.extra?.backendApiHML || '',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as BackendUrlState).auth?.userToken;
      if (token) {
        headers.set('Cookie', `access_token=${token}`);
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  });

export const backendApiSlice = createApi({
  reducerPath: 'backendApi',
  tagTypes: ['Favorite', 'Watching', 'Profile', 'Pin'],
  baseQuery: async (args, api, extraOptions) => {
    const state = api.getState() as BackendUrlState;
    const baseUrl = selectBackendBaseUrl(state) || Constants.expoConfig?.extra?.backendApiHML || '';
    return handleBaseQuery(rawBaseQuery(baseUrl))(args, api, extraOptions);
  },
  endpoints: (builder) => ({
    logIn: builder.mutation<BackendApiLogin, BackendApiLoginParams>({
      query: (payload) => ({
        url: '/api/login',
        method: 'POST',
        body: payload,
      }),
    }),
    getProfiles: builder.query<Profile[], void>({
      query: () => ({
        url: '/api/User/BandplayProfile',
        params: {
          sort: '-owner,createdAt',
        },
      }),
      transformResponse: (response: { items: BackendApiProfile[] }) => {
        return response?.items?.map(mapBackendProfileToFrontend) ?? [];
      },
      providesTags: ['Profile'],
    }),
    getProfileById: builder.query<Profile, string>({
      query: (id) => ({
        url: `/api/User/BandplayProfile/${id}`,
      }),
      transformResponse: (response: BackendApiProfile) => {
        return mapBackendProfileToFrontend(response);
      },
      providesTags: (result, error, id) => [{ type: 'Profile', id }],
    }),
    createProfile: builder.mutation<BackendApiProfile, Partial<Profile>>({
      query: (body) => ({
        url: '/api/User/BandplayProfile',
        method: 'POST',
        body: {
          name: body.name,
          rating: body.ageRating,
          owner: false,
          kids: false,
        },
      }),
      invalidatesTags: ['Profile'],
    }),
    updateProfile: builder.mutation<
      BackendApiProfile,
      { id: string; data: Partial<BackendApiProfile> }
    >({
      query: ({ id, data }) => ({
        url: `/api/User/BandplayProfile/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Profile', id }, 'Profile'],
    }),
    deleteProfile: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/User/BandplayProfile/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Profile', id }, 'Profile'],
    }),
    getPin: builder.query<BackendApiPin | null, void>({
      query: () => '/api/User/BandplayPin',
      transformResponse: (response: { items: BackendApiPin[]; total: number }) => {
        return response.items?.[0] ?? null;
      },
      providesTags: ['Pin'],
    }),
    createPin: builder.mutation<BackendApiPin, { user: string; pin: string }>({
      query: (body) => ({
        url: '/api/User/BandplayPin',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Pin'],
    }),
    updatePin: builder.mutation<BackendApiPin, { id: string; pin: string }>({
      query: ({ id, pin }) => ({
        url: `/api/User/BandplayPin/${id}`,
        method: 'PATCH',
        body: { pin },
      }),
      invalidatesTags: ['Pin'],
    }),
    deletePin: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/User/BandplayPin/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Pin'],
    }),
    resetPin: builder.mutation<{ pin: string }, void>({
      query: () => ({
        url: '/api/recovery-pin',
        method: 'GET',
      }),
      invalidatesTags: ['Pin'],
    }),
    getFavorites: builder.query<BackendGenericList<BackendFavorite>, string>({
      query: (profileId) => ({
        url: `/api/User/BandplayFavorite`,
        params: { profile: profileId },
      }),
      providesTags: (result, _error, _arg) =>
        result
          ? [...result.items.map(({ _id }) => ({ type: 'Favorite' as const, _id })), 'Favorite']
          : ['Favorite'],
    }),
    createFavorite: builder.mutation<BackendFavorite, Omit<BackendFavorite, '_id' | 'createdAt'>>({
      query: (payload) => ({
        url: '/api/User/BandplayFavorite',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Favorite'],
    }),
    deleteFavorite: builder.mutation<void, string>({
      query: (payload) => ({
        url: `/api/User/BandplayFavorite/${payload}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Favorite'],
    }),
    getWatchingList: builder.query<BackendGenericList<BackendWatching>, GetWatchingListParams>({
      query: ({ profileId, ...params }) => ({
        url: '/api/User/BandplayWatching',
        params: {
          profile: profileId,
          inProgress: true,
          ...params,
          limit: Platform.isTV ? 8 : 15,
        },
      }),
      providesTags: ['Watching'],
      keepUnusedDataFor: 0,
    }),
    createFeedback: builder.mutation<void, FeedbackProps>({
      query: (payload) => ({
        url: '/api/User/Feedback',
        method: 'POST',
        body: {
          ...payload,
          app: 'Bandplay',
        },
      }),
    }),
  }),
});

export const {
  useLogInMutation,
  useGetProfilesQuery,
  useGetProfileByIdQuery,
  useGetPinQuery,
  useGetFavoritesQuery,
  useGetWatchingListQuery,
  useLazyGetWatchingListQuery,
  useCreateProfileMutation,
  useUpdateProfileMutation,
  useDeleteProfileMutation,
  useCreatePinMutation,
  useUpdatePinMutation,
  useDeletePinMutation,
  useResetPinMutation,
  useCreateFavoriteMutation,
  useDeleteFavoriteMutation,
  useCreateFeedbackMutation,
} = backendApiSlice;
